import React, {
  ComponentType,
  useState
} from 'react'
import {
  Card,
  Form,
  Icon,
  Message
} from 'semantic-ui-react'
import isValidEmail from '../common/emailUtils'
import If from '../common/if'
import { shouldRenderReskin2025 } from '../../constant'
import ReveleerCoachingBox from '../ReveleerCoachingBox'
import LockVersionTwoLocked from '../icons/LockVersionTwoLocked'

const SafeCard = Card as unknown as ComponentType<any>
const SafeForm = Form as unknown as ComponentType<any>

type PasswordChange = {
  token: string
  userid: string
  email: string
  password: string
  confirmPassword: string
}

type UserPasswordChangeProps = {
  passwordChange: PasswordChange
  onSave: (passwordChange: PasswordChange) => void
  errors?: string[] | null
  setErrors: (x: string[] | null) => void
}

/**
 * Provides a password change form. The form provides basic validations and then
 * depends on the password reset API to complete validations
 * @param passwordChange Password change request entity
 * @param onSave Save callback. Argument: Password reset request
 * @param errors List of error messages. Default undefined
 */
function UserPasswordChange(props: UserPasswordChangeProps): JSX.Element {
  const {
    passwordChange: initialPasswordChange,
    onSave,
    errors,
    setErrors
  } = props

  const [ passwordChange, setPasswordChange ] = useState<PasswordChange>(initialPasswordChange)
  const [ isValidEmailAddress, setIsValidEmailAddress ] = useState<boolean>(false)

  const validate = (data: PasswordChange): string[] => {
    const validationErrors: string[] = []
    const { password, confirmPassword } = data

    if (!password || password.length === 0) {
      validationErrors.push('New password required')
    } else if (password && password.length < 7) {
      validationErrors.push('New password must be at least 7 characters long')
    } else if (!confirmPassword || confirmPassword !== password) {
      validationErrors.push('Password confirmation does not match')
    }
    return validationErrors
  }

  const handleSave = () => {
    const validationErrors = validate(passwordChange)
    if (validationErrors.length === 0) {
      onSave(passwordChange)
    } else {
      setErrors(validationErrors)
    }
  }

  const handleChange = (
    event: React.SyntheticEvent,
    data: {
      name: string
      value: string
    }
  ) => {
    const { name, value } = data
    const updatedPasswordChange = {
      ...passwordChange,
      [name]: value
    }
    const hasValidEmail = isValidEmail(updatedPasswordChange.email)
    setPasswordChange(updatedPasswordChange)
    setIsValidEmailAddress(hasValidEmail)
  }

  const { password, confirmPassword } = passwordChange
  const shouldShowSave = password
    && password.length > 0
    && confirmPassword
    && confirmPassword.length > 0
    && password.length === confirmPassword.length
    && isValidEmailAddress

  return (
    <div>
      <SafeCard>
        <Card.Content>
          <Card.Header>
            <span>
              <If isTrue={ !shouldRenderReskin2025 }>
                <Icon name="lock" /> Password Requirements
              </If>
              <If isTrue={ shouldRenderReskin2025 }>
                <span className="m-8px-right">
                  <LockVersionTwoLocked />
                </span>
                Password Requirements
              </If>
            </span>
          </Card.Header>
        </Card.Content>
        <Card.Content>
          <ul>
            <li>Your password must be at least seven characters long</li>
            <li>It must contain 1 each of the following:
              <ul>
                <li>Upper case letter</li>
                <li>Lower case letter</li>
                <li>Number</li>
                <li>Special character</li>
              </ul>
            </li>
            <li>
              Passwords must not be based on something that can be easily guessed or obtained using
              personal information (e.g. names, favorite sports team, etc.)
            </li>
            <li>Passwords must not use four or more sequential or repetitive characters</li>
            <li>Passwords that have been compromised in a data breach must not be used</li>
          </ul>
        </Card.Content>
        <Card.Content>
          <span id="password-reset-log-back-in">
            <Icon name="info circle" />
            { ' ' }
            To keep your account secure, changing your password will require you to log back in to continue working.
          </span>
        </Card.Content>
      </SafeCard>
      <If isTrue={ !!errors }>
        <div className="m-1-top m-1-bottom">
          {
            errors
              ? (
                <>
                  <If isTrue={ !shouldRenderReskin2025 }>
                    <Message
                      negative
                      list={ errors }
                    />
                  </If>
                  <If isTrue={ shouldRenderReskin2025 }>
                    <ReveleerCoachingBox
                      coachingBoxType="error"
                      isShowing
                      message={
                        <p>
                          { errors }
                        </p>
                      }
                    />
                  </If>
                </>
              )
              : null
          }
        </div>
      </If>
      <SafeForm>
        <Form.Input
          label="Email"
          placeholder="Email"
          name="email"
          value={ passwordChange.email }
          onChange={ handleChange }
        />
        <Form.Input
          label="Password"
          type="password"
          placeholder="Password"
          name="password"
          value={ passwordChange.password }
          onChange={ handleChange }
        />
        <Form.Input
          label="Confirm Password"
          type="password"
          placeholder="Confirm Password"
          name="confirmPassword"
          value={ passwordChange.confirmPassword }
          onChange={ handleChange }
        />
        <If isTrue={ !!shouldShowSave }>
          <Form.Button
            content="Save"
            onClick={ handleSave }
          />
        </If>
      </SafeForm>
    </div>
  )
}

export default UserPasswordChange
